import type {
  CreateUploadTransferJobPayload,
  ExportSequencePayload,
  ExportSettings,
  LastViewedPath,
  MarkerSyncStatus,
  PremiereExportPreset,
  PremiereProject,
  PremiereSequence,
  SharedAuthTokens,
  TransferJobAsset,
  UxpHostInfo,
} from '../generated/types';

type LocalStorageKeyMap = {
  lastViewedPath: string;
};

type LocalStorageKey = keyof LocalStorageKeyMap;

export const setItem = <K extends LocalStorageKey>(
  key: K,
  value: LocalStorageKeyMap[K]
): void => {
  // eslint-disable-next-line no-storage/no-browser-storage
  localStorage.setItem(key, JSON.stringify(value));
};

export const getItem = <K extends LocalStorageKey>(
  key: K,
  defaultValue: LocalStorageKeyMap[K]
): LocalStorageKeyMap[K] => {
  // eslint-disable-next-line no-storage/no-browser-storage
  const item = localStorage.getItem(key);
  if (item === null) return defaultValue;
  try {
    return JSON.parse(item) as LocalStorageKeyMap[K];
  } catch {
    return defaultValue;
  }
};

export const mockStorage = {
  getItem,
  setItem,
};

export const sequence = (): PremiereSequence => {
  return {
    id: 'mock-sequence-id',
    name: 'Mock Sequence',
    metadata: [],
    end: 0,
    inPoint: 0,
    outPoint: 0,
    start: 0,
    __typename: 'PremiereSequence',
  };
};

export const activeProject = (): PremiereProject => {
  return {
    id: 'mock-project-id',
    name: 'Mock Project',
    activeSequence: sequence(),
  };
};

export const lastViewedPath = (): LastViewedPath => {
  return {
    __typename: 'LastViewedPath',
    path: mockStorage.getItem('lastViewedPath', '/workspaces'),
  };
};

export const getTheme = (): string => {
  return 'darkest';
};

export const uxpHostInfo = (): UxpHostInfo => {
  return {
    __typename: 'UXPHostInfo',
    name: 'premierepro',
    pluginVersion: '0.0.1',
    schemaVersion: '0.0.1',
    uiLocale: 'en-US',
    uxpVersion: '0.0.1',
    version: '25.4.0',
    build: null,
  };
};

export const exportPresets = (): PremiereExportPreset[] => {
  return [
    {
      id: '1',
      name: 'Test Preset #1',
      filePath: '/test/path',
      presetSummaryJSON: '{}',
      isCustom: true,
      isFavorite: false,
      format: 'H.264',
      // description: 'A test export preset',
      __typename: 'PremiereExportPreset',
    },
    {
      id: '2',
      name: 'Test Preset #2',
      filePath: '/test/path2',
      presetSummaryJSON: '{}',
      isCustom: false,
      isFavorite: true,
      format: 'H.264',
      // description: 'Another test export preset',
      __typename: 'PremiereExportPreset',
    },
  ];
};

export const exportSequence = (): ExportSequencePayload => {
  return {
    filePath: '/test/export.mp4',
    batchItemId: 'test-batch-id',
    __typename: 'ExportSequencePayload',
  };
};

export const exportSettings = (): ExportSettings => {
  return {
    localCopyEnabled: true,
    localFolderPath: '/test/path',
    shareEnabled: true,
    shareDownloadsEnabled: true,
    shareCommentsEnabled: true,
    assetFolderId: 'test-folder-id',
    assetFolderName: 'Test Folder',
    presetId: 'test-preset-id',
    range: 'entireSequence',
    exportMarkersAsCommentsEnabled: false,
    __typename: 'ExportSettings',
  };
};

export const sharedAuthTokens = (): SharedAuthTokens => {
  return {
    accessToken: 'mock-access-token',
    refreshToken: null,
    __typename: 'SharedAuthTokens',
  };
};

export const markerSyncStatus = (): MarkerSyncStatus => {
  return {
    assetId: 'test-asset-id',
    sequenceId: 'test-sequence-id',
    status: 'ENABLED',
    __typename: 'MarkerSyncStatus',
  };
};

export const transferJobAsset = (): TransferJobAsset => {
  return {
    id: 1,
    filesize: 1000,
    fileType: 'mp4',
    name: 'test.mp4',
    type: 'video',
    __typename: 'TransferJobAsset',
  };
};

export const createUploadTransferJob = (): CreateUploadTransferJobPayload => {
  return {
    id: 'test-job-id',
    name: 'Test Upload Job',
    assets: [transferJobAsset()],
    __typename: 'CreateUploadTransferJobPayload',
  };
};

export const isSignedIn = (): boolean => {
  return false;
};

export const localFolderPath = (): string => {
  return '/test/path';
};
